import React, { useEffect, useRef, useState } from "react";
import "./TaskHistory.css";
import { HelenService } from "../../services/helen/helenService";
import { TaskService } from "../../services/task/taskService";
import { TaskGroup, TaskItem } from "../Task/Task";
import ProductDetailsPopup from "../../components/ProductDetailsPopup";
import Loader from "../../components/common/Loader/Loader";
import TopBar from "../../components/TopBar/TopBar";

interface TaskHistoryProps {
  activeTab: string;
}

const TaskHistory: React.FC<TaskHistoryProps> = ({ activeTab }) => {
  const [isTaskHistoryLoading, seIsTaskHistoryLoading] = useState(true);
  const [isBotsLoading, setIsBotsLoading] = useState(true);
  const [selectedHelen, setSelectedHelen] = useState<any>(null);
  const [taskHistory, setTaskHistory] = useState<TaskGroup[]>([]);
  const [viewedTask, setViewedTask] = useState<TaskItem | null>(null);
  const [botsAssignedToUser, setBotsAssignedToUser] = useState<any>(null);
  const [expandedTasks, setExpandedTasks] = useState<number[]>([]);
  const popupRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [timePeriod, setTimePeriod] = useState("Current Month");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortField, setSortField] = useState<string>("created_at");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = useState("");
  const [status, setStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<{
    current_page: number;
    limit: number;
    total_count: number;
    returned_count: number;
    start_item: number;
    end_item: number;
    total_pages: number;
  }>({
    current_page: 1,
    limit: 10,
    total_count: 0,
    returned_count: 0,
    start_item: 0,
    end_item: 0,
    total_pages: 0,
  });

  useEffect(() => {
    const fetchBots = async () => {
      try {
        setIsBotsLoading(true);
        const resp = await HelenService.getBotsAssignedToUser("all");
        const bots = resp?.data?.userAssignedBots || [];
        setBotsAssignedToUser(bots);

        if (bots.length > 0) {
          setSelectedHelen(bots[0]);
        }
      } catch (error) {
      } finally {
        setIsBotsLoading(false);
      }
    };

    fetchBots();
  }, []);

  // Immediate fetch on filter/sort/pagination change (exclude selectedHelen)
  useEffect(() => {
    if (selectedHelen) {
      fetchTaskHistory();
    }
  }, [currentPage, pageSize, sortField, sortDirection]);

  // Fetch immediately when selecting Helen
  useEffect(() => {
    if (selectedHelen) {
      setCurrentPage(1); // optional: reset page
      fetchTaskHistory();
    }
  }, [selectedHelen]);

  // Debounced search
  useEffect(() => {
    const handler = setTimeout(() => {
      if (selectedHelen) fetchTaskHistory(searchTerm);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        popupRef.current && // now it's HTMLDivElement
        !popupRef.current.contains(e.target as Node) &&
        !buttonRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchTaskHistory = async (term: string = "") => {
    if (!selectedHelen) return;

    // Only search if term has >= 2 characters
    const search = term.length >= 2 ? term : "";

    seIsTaskHistoryLoading(true);

    try {
      const { startDate, endDate } = getDateRange();

      const taskResp = await TaskService.getTaskHistory(
        Number(selectedHelen.id),
        currentPage,
        pageSize,
        status,
        startDate,
        endDate,
        sortField,
        sortDirection,
        search
      );

      const taskGroups: TaskGroup[] = taskResp.data.data.map((parent: any) => ({
        parentTask: parent,
        children: parent.children || [],
      }));

      setTaskHistory(taskGroups);

      const pag = taskResp.data.pagination;
      setPagination({
        current_page: pag.currentPage,
        limit: pag.pageSize,
        total_count: pag.totalCount,
        returned_count: taskGroups.length,
        start_item: (pag.currentPage - 1) * pag.pageSize + 1,
        end_item: (pag.currentPage - 1) * pag.pageSize + taskGroups.length,
        total_pages: pag.totalPages,
      });
    } catch (error) {
      setTaskHistory([]);
      setPagination({
        current_page: 1,
        limit: pageSize,
        total_count: 0,
        returned_count: 0,
        start_item: 0,
        end_item: 0,
        total_pages: 0,
      });
    } finally {
      seIsTaskHistoryLoading(false);
    }
  };

  const getDateRange = (): { startDate: string; endDate: string } => {
    const now = new Date();
    let startDate: Date, endDate: Date;

    switch (timePeriod) {
      case "Current Month":
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case "Last Month":
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case "Last 3 Months":
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case "Custom":
        if (fromDate && toDate) {
          startDate = new Date(fromDate);
          endDate = new Date(toDate);
        } else {
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        }
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }

    return {
      startDate: startDate.toISOString().split("T")[0],
      endDate: endDate.toISOString().split("T")[0],
    };
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => prev + 1);
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1); // Reset to first page
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1); // Reset to page 1
  };

  const toggleTask = (taskId: number) => {
    setExpandedTasks((prev) =>
      prev.includes(taskId)
        ? prev.filter((id) => id !== taskId)
        : [...prev, taskId]
    );
  };

  const STATUS_LABELS: Record<string, string> = {
    pending: "Pending",
    queued: "Queued",

    processing: "Processing",
    completed: "Completed",
    failed: "Failed",
    cancelled: "Cancelled",
    onhold: "On Hold",
  };

  const handleDownload = async (taskId: string) => {
    try {
      const res = await TaskService.exportTaskToExcel(taskId);

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tasks_${taskId}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
    }
  };

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

  function formatDateTime(input: string | null | undefined): string {
    if (!input) return "--";

    const date = new Date(input);
    if (isNaN(date.getTime())) return "--";

    const options: Intl.DateTimeFormatOptions = {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false, // this gives 24-hour time
    };

    return date.toLocaleString("en-GB", options).replace(",", "");
  }

  if (isBotsLoading)
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Loader inline={true} />
      </div>
    );

  return (
    <div className="task-history-container">
      <TopBar />
      <h1 className="th-page-title">Helen History</h1>
      <div className="th-tabs-row">
        {botsAssignedToUser?.map((helen: any) => (
          <button
            key={helen.id}
            className={`th-tab-btn ${helen === selectedHelen ? "active-tab" : ""}`}
            onClick={() => {
              setSelectedHelen(helen);
              setCurrentPage(1);
            }}
          >
            {helen.helen_name.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="history-header">
        <h2 className="history-title">Task History</h2>
        <div className="history-header-right">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => {
              setCurrentPage(1);
              setSearchTerm(e.target.value);
            }}
          />
          <div className="filter-wrapper">
            <button
              className="filter-button"
              onClick={() => setOpen((prev) => !prev)}
            >
              Filter ▼
            </button>

            {open && (
              <div className="filter-popup" ref={popupRef}>
                <div className="popup-header">
                  <span className="title">Filter</span>
                  <button className="close-btn" onClick={() => setOpen(false)}>
                    ✕
                  </button>
                </div>

                {/* Status Filter */}
                <div className="filter-row">
                  <div className="row-head">
                    <label>STATUS</label>
                    <button
                      type="button"
                      className="reset-link"
                      onClick={() => setStatus("All")}
                    >
                      Reset
                    </button>
                  </div>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                  >
                    <option value={"all"}>All</option>
                    <option value={"completed"}>Completed</option>
                    <option value={"cancelled"}>Cancelled</option>
                  </select>
                </div>

                {/* Time Period */}
                <div className="filter-row">
                  <div className="row-head">
                    <label>TIME PERIOD</label>
                    <button
                      type="button"
                      className="reset-link"
                      onClick={() => setTimePeriod("Current Month")}
                    >
                      Reset
                    </button>
                  </div>
                  <select
                    value={timePeriod}
                    onChange={(e) => setTimePeriod(e.target.value)}
                  >
                    <option>Current Month</option>
                    <option>Last Month</option>
                    <option>Last 3 Months</option>
                    <option>Custom</option>
                  </select>
                </div>

                {/* Custom Date Range */}
                {timePeriod === "Custom" && (
                  <div className="filter-row">
                    <div className="row-head">
                      <label>DATE RANGE</label>
                      <button
                        type="button"
                        className="reset-link"
                        onClick={() => {
                          setFromDate("");
                          setToDate("");
                        }}
                      >
                        Reset
                      </button>
                    </div>
                    <div className="date-range">
                      <div className="date-field">
                        <label htmlFor="fromDate">From:</label>
                        <input
                          id="fromDate"
                          type="date"
                          value={fromDate}
                          max={toDate ? toDate : undefined}
                          onChange={(e) => setFromDate(e.target.value)}
                        />
                      </div>
                      <div className="date-field">
                        <label htmlFor="toDate">To:</label>
                        <input
                          id="toDate"
                          type="date"
                          value={toDate}
                          min={fromDate ? fromDate : undefined}
                          onChange={(e) => setToDate(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="popup-actions">
                  <button
                    className="reset-btn"
                    onClick={() => {
                      setStatus("All");
                      setTimePeriod("Current Month");
                      setFromDate("");
                      setToDate("");
                    }}
                  >
                    Reset
                  </button>

                  <button
                    className="apply-btn"
                    onClick={() => {
                      fetchTaskHistory(); // fetch with current filter states
                      setOpen(false); // close popup
                    }}
                  >
                    Apply Now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card-table">
        <table className="task-history-table">
          <colgroup>
            <col style={{ width: "3%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "20%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "6%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "7%" }} />
          </colgroup>
          <thead>
            <tr>
              <th></th>

              <th onClick={() => handleSort("taskNumber")}>
                TASK NUMBER{sortField === "taskNumber" && <span className="sort">{sortDirection === "asc" ? "▲" : "▼"}</span>}
              </th>
              <th onClick={() => handleSort("productTitle")}>
                NAME{sortField === "productTitle" && <span className="sort">{sortDirection === "asc" ? "▲" : "▼"}</span>}
              </th>
              <th onClick={() => handleSort("type")}>
                TYPE{sortField === "type" && <span className="sort">{sortDirection === "asc" ? "▲" : "▼"}</span>}
              </th>
              <th onClick={() => handleSort("count")}>
                COUNT{sortField === "count" && <span className="sort">{sortDirection === "asc" ? "▲" : "▼"}</span>}
              </th>
              <th onClick={() => handleSort("created_at")}>
                UPLOADED ON{sortField === "created_at" && <span className="sort">{sortDirection === "asc" ? "▲" : "▼"}</span>}
              </th>
              <th onClick={() => handleSort("executed_at")}>
                PROCESS TIME{sortField === "executed_at" && <span className="sort">{sortDirection === "asc" ? "▲" : "▼"}</span>}
              </th>
              <th onClick={() => handleSort("status")}>
                STATUS{sortField === "status" && <span className="sort">{sortDirection === "asc" ? "▲" : "▼"}</span>}
              </th>
              <th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {isTaskHistoryLoading ? (
              <tr>
                <td colSpan={9} className="tbody-loader-cell">
                  <div className="tbody-spinner" />
                </td>
              </tr>
            ) : taskHistory.length === 0 ? (
              <tr className="empty-row">
                <td colSpan={9}>No tasks found.</td>
              </tr>
            ) : (
              taskHistory.map((group, index) => {
                const parent = group.parentTask;
                const isExpanded = expandedTasks.includes(parent.id);
                const hasChildren = group.children.length > 0;

                return (
                  <React.Fragment key={parent.id}>
                    <tr>
                      <td>
                        {hasChildren ? (
                          <button
                            className="expand-button"
                            onClick={() => toggleTask(parent.id)}
                            aria-label={isExpanded ? "Collapse" : "Expand"}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? (
                              <img src="/images/uparrow.svg" alt="Collapse" />
                            ) : (
                              <img src="/images/downarrow.svg" alt="Expand" />
                            )}
                          </button>
                        ) : null}
                      </td>
                      <td className="cell-task-num">{parent.taskNumber}</td>
                      <td className="cell-name">{parent.productTitle}</td>
                      <td className="cell-type">{parent.taskType}</td>
                      <td>
                        <span className="count-chip">{group.children.length || 1}</span>
                      </td>
                      <td>{formatDateTime(parent.createdAt)}</td>
                      <td>{formatDateTime(parent.executedAt)}</td>
                      <td>
                        <span className={`status-badge ${parent.status.toLowerCase()}`}>
                          {STATUS_LABELS[parent.status.toLowerCase()]}
                        </span>
                      </td>
                      <td>
                        {group.children.length > 0 &&
                        parent?.status?.toLowerCase() !== "cancelled" &&
                        parent?.status?.toLowerCase() !== "failed" ? (
                          <button
                            className="th-action-btn"
                            title="Download"
                            onClick={() => handleDownload(String(parent.id))}
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          </button>
                        ) : (
                          parent?.status?.toLowerCase() !== "cancelled" &&
                          parent?.status?.toLowerCase() !== "failed" && (
                            <button
                              className="th-action-btn"
                              title="View"
                              onClick={() => setViewedTask(parent)}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            </button>
                          )
                        )}
                      </td>
                    </tr>

                    {isExpanded &&
                      group.children.map((child) => (
                        <tr key={child.id} style={{ background: "#FAFBFF" }}>
                          <td></td>
                          <td className="cell-task-num">{child.taskNumber}</td>
                          <td className="cell-name">{child.productTitle}</td>
                          <td className="cell-type">{child.taskType}</td>
                          <td><span className="count-chip">1</span></td>
                          <td>{formatDateTime(child.createdAt)}</td>
                          <td>{formatDateTime(child.executedAt)}</td>
                          <td>
                            <span className={`status-badge ${child.status.toLowerCase()}`}>
                              {STATUS_LABELS[child.status.toLowerCase()]}
                            </span>
                          </td>
                          <td>
                            {child?.status?.toLowerCase() !== "cancelled" &&
                              child?.status?.toLowerCase() !== "failed" && (
                                <button
                                  className="th-action-btn"
                                  title="View"
                                  onClick={() => setViewedTask(child)}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                </button>
                              )}
                          </td>
                        </tr>
                      ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-footer" style={{ marginTop: "12px" }}>
        <div className="pagination-right">
          <div className="pagination-info">
            Page {pagination.current_page} of {pagination.total_pages}{" "}
            &nbsp;|&nbsp;
            {pagination.start_item} - {pagination.end_item} out of{" "}
            {pagination.total_count} results found
          </div>
          <div className="pagination-pagesize">
            <select value={pageSize} onChange={handlePageSizeChange}>
              <option value={10}>10</option>
              <option value={30}>30</option>
              <option value={60}>60</option>
              <option value={90}>90</option>
            </select>
          </div>
        </div>
        <div className="pagination-controls">
          <button
            onClick={handlePrevPage}
            disabled={pagination.current_page === 1}
            className="pagination-btn"
          >
            <img src="/images/PreviousButton.svg" alt="Previous" />
          </button>
          <button
            onClick={handleNextPage}
            disabled={pagination.current_page >= pagination.total_pages}
            className="pagination-btn"
          >
            <img src="/images/NextButton.svg" alt="Next" />
          </button>
        </div>
      </div>

      {viewedTask && (
        <ProductDetailsPopup
          taskItem={viewedTask}
          onClose={() => setViewedTask(null)}
        />
      )}
    </div>
  );
};

export default TaskHistory;
